import React from "react";
import SettingsSectionIntro from "./SettingsSectionIntro";
import SettingsCard from "./SettingsCard";

export default function AdminSettingsPage({
  user,
  onNavigateToAdmin,
  whatsAppConfig,
  loadingWhatsAppConfig,
  savingWhatsAppConfig,
  whatsAppConfigError,
  whatsAppConfigSuccess,
  onWhatsAppConfigChange,
  onSaveWhatsAppConfig,
}) {
  const isAdmin = user?.is_staff || user?.is_superuser;

  return (
    <div className="flex flex-col gap-4 w-full">
      <SettingsSectionIntro
        title="Admin Settings"
        description="Manage administrative access and privileged configuration areas."
      />
      <SettingsCard title="Administrative Access">
        {isAdmin ? (
          <div className="px-6 py-6 grid gap-4 md:grid-cols-[1.4fr_auto] md:items-center">
            <div className="space-y-2">
              <p className="font-inter font-semibold text-sm text-gray-900">Admin Dashboard Access</p>
              <p className="font-inter text-sm text-gray-500 leading-relaxed">
                You have administrative privileges. Access the admin dashboard to manage users and system settings.
              </p>
            </div>
            <div>
              <button
                onClick={onNavigateToAdmin}
                className="w-full md:w-auto mt-1 px-5 py-2.5 bg-gradient-to-r from-primary-blue to-blue-400 text-white text-sm font-inter font-semibold rounded-lg hover:from-blue-600 hover:to-blue-500 transition-colors shadow-sm"
              >
                Go to Admin Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-6">
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
              <p className="font-inter text-sm text-red-600">
                You don't have administrative access. Please contact your system administrator for elevated privileges.
              </p>
            </div>
          </div>
        )}
      </SettingsCard>

      {isAdmin && (
        <SettingsCard title="WhatsApp Welcome Message">
          <div className="px-6 py-6 flex flex-col gap-4">
            <p className="font-inter text-sm text-gray-500 leading-relaxed">
              Customize the welcome message sent automatically to new WhatsApp contacts on their first message.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={whatsAppConfig?.is_welcome_enabled}
                onClick={() =>
                  onWhatsAppConfigChange &&
                  onWhatsAppConfigChange("is_welcome_enabled", !whatsAppConfig?.is_welcome_enabled)
                }
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  whatsAppConfig?.is_welcome_enabled ? "bg-primary-blue" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    whatsAppConfig?.is_welcome_enabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="font-inter text-sm text-gray-700">
                {whatsAppConfig?.is_welcome_enabled ? "Welcome message enabled" : "Welcome message disabled"}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-inter text-sm font-medium text-gray-700">
                Welcome Message
              </label>
              {loadingWhatsAppConfig ? (
                <div className="h-40 rounded-lg bg-gray-100 animate-pulse" />
              ) : (
                <textarea
                  rows={8}
                  value={whatsAppConfig?.welcome_message || ""}
                  onChange={(e) =>
                    onWhatsAppConfigChange && onWhatsAppConfigChange("welcome_message", e.target.value)
                  }
                  disabled={!whatsAppConfig?.is_welcome_enabled}
                  className="w-full rounded-lg border border-gray-200 px-4 py-3 font-inter text-sm text-gray-900 placeholder-gray-400 focus:border-primary-blue focus:outline-none focus:ring-1 focus:ring-primary-blue disabled:bg-gray-50 disabled:text-gray-400 resize-y"
                  placeholder="Enter welcome message..."
                />
              )}
              <p className="font-inter text-xs text-gray-400">
                Supports WhatsApp formatting: *bold*, _italic_. Keep under 1600 characters.
              </p>
            </div>

            {whatsAppConfigError && (
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
                <p className="font-inter text-sm text-red-600">{whatsAppConfigError}</p>
              </div>
            )}

            {whatsAppConfigSuccess && (
              <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3">
                <p className="font-inter text-sm text-green-600">{whatsAppConfigSuccess}</p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onSaveWhatsAppConfig}
                disabled={savingWhatsAppConfig || loadingWhatsAppConfig}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-blue to-blue-400 text-white text-sm font-inter font-semibold rounded-lg hover:from-blue-600 hover:to-blue-500 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingWhatsAppConfig ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
